import { Mod } from '@components/arsenal/mods/Mod';
import { useAppStore, UserData } from '@stores';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/ui/Modal';
import { useFuseModsMutation } from '@http';

interface ModFuseProps {
  mod: UserData['mods'][0];
  open: boolean;
  onClose: () => void;
};

export function ModFuse({ mod, open, onClose }: ModFuseProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Mods' });
  const { mutateAsync } = useFuseModsMutation();
  const { userData, isFetching, setIsFetching, refetch } = useAppStore();

  const similarMods = userData.mods
    .filter(m => m.key === mod.key && m.rank === mod.rank)
    .filter(m => !userData.weapons.some(w => w.mods.some(wm => wm.modId === m.id)))
    .filter(m => !userData.chassis.some(c => c.mods.some(cm => cm.modId === m.id)));
  const credits = mod.rank === 0 ? 10_000 : 60_000;
  const canAfford = userData.credits >= credits;

  const fuse = async () => {
    if (isFetching) return;
    setIsFetching(true);

    await mutateAsync({ id: mod.id });
    refetch();
    onClose();
  };

  return (
    <Modal onRequestClose={onClose} title={t('fuseModal')} isOpen={open}>
      <div className="flex flex-col items-center p-4">
        <Mod previewLevel={mod.level} mod={mod} />

        {mod.rank >= 2 && (
          <p className="text-center text-sm mt-4 text-gray-400">
            {t('fuseMaxRank')}
          </p>
        )}

        {similarMods.length < 4 && mod.rank < 2 && (
          <p className="text-center text-sm mt-4 text-gray-400">
            {t('fuseNotEnough', { amount: similarMods.length })}
          </p>
        )}

        {similarMods.length >= 4 && mod.rank < 2 && (
          <>
            <p className="text-center text-sm text-shield mt-4">
              {t('fuseDescription')}
            </p>

            <button className="btn btn-active flex items-center justify-between mt-4" disabled={!canAfford} onClick={fuse}>
              <p>{t('fuse')}</p>
      
              <div className="flex items-center">
                <p className={`${canAfford ? 'text-black' : 'text-red-500'} text-sm text-mono`}>
                  {credits}
                </p>
                <img src="/assets/items/credit.avif" className="h-4.5 ml-1" alt="credit" />
              </div>
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}